import {Button, Input, Modal} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useState} from 'react'
import {BiTrash} from 'react-icons/bi'
import {PiPlusBold} from 'react-icons/pi'

// Mirror TimetableDetailSongsForm.js - quản lý 0-N link tài liệu bài giảng của 1 buổi học cụ thể,
// chỉ khác field (title + url thay vì music_id/song_name/part), không cần LookupField (URL gõ tay).
//
// Tách rows-state (useMaterialRows) + body markup (MaterialRowsFields) khỏi Modal wrapper để dùng lại
// được ở nơi khác cần chrome khác Modal (vd DrawDialog trong MyClassSessionsPanel.js) mà không phải
// chép lại logic add/remove/build-payload.

const emptyRow = () => ({_key: `${Date.now()}_${Math.random()}`, id: undefined, title: '', url: ''})

const materialToRow = (material) => ({
    _key: material.id,
    id: material.id,
    title: material.title || '',
    url: material.url || '',
})

export const useMaterialRows = (materials, active) => {
    const [rows, setRows] = useState([])

    useEffect(() => {
        if (active) setRows((materials || []).map(materialToRow))
    }, [active, materials])

    const setRow = (key, updates) => setRows((r) => r.map((row) => (row._key === key ? {...row, ...updates} : row)))
    const removeRow = (key) => setRows((r) => r.filter((row) => row._key !== key))
    const addRow = () => setRows((r) => [...r, emptyRow()])

    const buildPayload = () =>
        rows
            .filter((r) => r.url && r.url.trim())
            .map((r, index) =>
                _.omitBy(
                    {
                        id: r.id,
                        title: r.title || null,
                        url: r.url.trim(),
                        position: index,
                    },
                    _.isUndefined
                )
            )

    return {rows, setRow, removeRow, addRow, buildPayload}
}

export const MaterialRowsFields = ({rows, setRow, removeRow, addRow}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-3">
            {rows.length === 0 && (
                <div className="text-xs text-color-400 italic text-center py-3">{t('no materials yet')}</div>
            )}
            {rows.map((row) => (
                <div key={row._key} className="flex flex-col gap-1.5 p-2 border border-color-50 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Input
                            className="flex-1 min-w-0"
                            placeholder={t('material title placeholder')}
                            value={row.title}
                            onChange={(e) => setRow(row._key, {title: e.target.value})}
                        />
                        <button
                            type="button"
                            onClick={() => removeRow(row._key)}
                            className="flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-primary-50"
                            style={{width: 36, height: 36}}>
                            <BiTrash size={15} className="text-primary-400"/>
                        </button>
                    </div>
                    <Input
                        placeholder={t('material url placeholder')}
                        value={row.url}
                        onChange={(e) => setRow(row._key, {url: e.target.value})}
                    />
                </div>
            ))}
            <Button
                color="primary"
                variant="filled"
                className="flex flex-center font-medium text-xs rounded-lg self-start"
                onClick={addRow}>
                <PiPlusBold size={11} className="mr-1"/>
                {t('add material link')}
            </Button>
        </div>
    )
}

export const TimetableDetailMaterialsForm = ({visible, item, loading, onSubmit, onCancel}) => {
    const t = useTranslate()
    const {rows, setRow, removeRow, addRow, buildPayload} = useMaterialRows(item?.materials, visible)

    return (
        <Modal
            className="custom-modal"
            open={visible}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('manage lecture materials')}
                </div>
            }
            onCancel={onCancel}
            onOk={() => onSubmit(buildPayload())}
            okText={t('save')}
            cancelText={t('cancel')}
            confirmLoading={loading}
            width={480}
            destroyOnHidden
            centered>
            <MaterialRowsFields rows={rows} setRow={setRow} removeRow={removeRow} addRow={addRow}/>
        </Modal>
    )
}
